import { useTranslation } from 'react-i18next';
import type { SkillInfo } from '../../types/agent';

/** The Skills a draft starting with `/` may name: a name starting with what follows the slash first, then a name
 *  containing it. */
export function matchSkills(skills: SkillInfo[], query: string): SkillInfo[] {
  const q = query.toLowerCase();
  const starts = skills.filter((s) => s.name.toLowerCase().startsWith(q));
  const contains = skills.filter((s) => !s.name.toLowerCase().startsWith(q) && s.name.toLowerCase().includes(q));
  return [...starts, ...contains];
}

/** The composer's `/` menu (sculpt-api sculpt/skills/SPEC.md): the Skills above the input box, each with its
 *  description. Picking one writes `/<name> ` at the start of the draft; the agent reads the Skill the message names. */
export function SkillMenu({ skills, active, onPick, onHover }: { skills: SkillInfo[]; active: number; onPick: (name: string) => void; onHover: (index: number) => void }) {
  const { t } = useTranslation();
  return (
    <div role="listbox" aria-label={t('skills.menu')} data-testid="skill-menu" className="absolute bottom-[calc(100%+6px)] left-0 right-0 z-20 flex max-h-[240px] animate-pc-in-fast flex-col overflow-y-auto rounded-md border border-line bg-white p-1 shadow-[0_6px_20px_rgba(0,0,0,.08)]">
      <div className="px-2 pb-1 pt-1 text-[11px] leading-snug text-faint">{t('skills.menu')}</div>
      {skills.map((s, i) => (
        <button
          key={s.name}
          type="button"
          role="option"
          aria-selected={i === active}
          // Keep the focus in the textarea: a click would blur it before the pick.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(s.name)}
          onMouseEnter={() => onHover(i)}
          className={`flex flex-col gap-0.5 rounded-sm px-2 py-1.5 text-left ${i === active ? 'bg-hover-soft' : ''}`}
        >
          <span className="font-mono text-[12px] text-ink">/{s.name}</span>
          <span className="line-clamp-2 text-[11px] leading-snug text-muted">{s.description}</span>
        </button>
      ))}
    </div>
  );
}
